import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { 
  RefreshCw, 
  Play, 
  Square,
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Settings,
  Database,
  Calendar,
  Filter,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

const BronzeLayer = () => {
  // Every write below is admin-only server-side; a non-admin reaches this page
  // with the datasync feature and gets the read-only view.
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState([]);
  // The API returns only the most recent sync states per data type; this holds how many
  // exist in total, keyed by `source:data_type`, so groups can show "showing X of Y".
  const [availableSyncStates, setAvailableSyncStates] = useState({});
  const [syncConfigs, setSyncConfigs] = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [activeTab, setActiveTab] = useState('status');
  const [filterSource, setFilterSource] = useState('');
  const [filterDataType, setFilterDataType] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusResponse, configsResponse, healthResponse] = await Promise.all([
        axios.get('/api/bronze/sync/status'),
        axios.get('/api/bronze/sync/configs'),
        axios.get('/api/bronze/sync/health').catch(() => ({ data: null }))
      ]);
      
      // Sort sync status by created_at (newest first)
      const sortedSyncStatus = (statusResponse.data.sync_status || []).sort((a, b) => {
        const dateA = new Date(a.created_at || a.last_sync || 0);
        const dateB = new Date(b.created_at || b.last_sync || 0);
        return dateB - dateA; // Newest first
      });
      
      // Totals behind the per-data-type cap, summed across tenants of the same data type
      const availableCounts = (statusResponse.data.sync_state_counts || []).reduce((counts, entry) => {
        const key = `${entry.source}:${entry.data_type}`;
        counts[key] = (counts[key] || 0) + (entry.available || 0);
        return counts;
      }, {});

      // Sort sync configs by source, then by data_type
      const sortedSyncConfigs = (configsResponse.data.configurations || []).sort((a, b) => {
        // First sort by source
        if (a.source !== b.source) {
          return a.source.localeCompare(b.source);
        }
        // Then sort by data_type
        return (a.data_type || '').localeCompare(b.data_type || '');
      });
      
      setSyncStatus(sortedSyncStatus);
      setAvailableSyncStates(availableCounts);
      setSyncConfigs(sortedSyncConfigs);
      setHealthData(healthResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch bronze layer data');
    } finally {
      setLoading(false);
    }
  };

  const startSync = async (source, dataType = null) => {
    try {
      // Use the correct endpoint format: /bronze/sync/{source}/{data_type}
      const endpoint = dataType 
        ? `/api/bronze/sync/${source}/${dataType}`
        : `/api/bronze/sync/${source}`;
      
      await axios.post(endpoint);
      toast.success(`Started sync for ${source}${dataType ? ` - ${dataType}` : ''}`);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start sync');
    }
  };

  const toggleConfig = async (configId) => {
    try {
      await axios.post(`/api/bronze/sync/configs/${configId}/toggle`);
      toast.success('Configuration updated');
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Failed to toggle configuration');
    }
  };

  const toggleGroup = (groupKey) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
      console.log('groupKey', groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'failed':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case 'in_progress':
        return <Clock className="h-5 w-5 text-blue-600" />;
      default:
        return <Clock className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'success':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'failed':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'in_progress':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getSyncTypeBadge = (syncType) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (syncType) {
      case 'full':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case 'incremental':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getAppliedToOperationalBadge = (isApplied) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    if (isApplied) {
      return `${baseClasses} bg-green-100 text-green-800`;
    } else {
      return `${baseClasses} bg-gray-100 text-gray-600`;
    }
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'healthy': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'critical': return AlertTriangle;
      default: return Clock;
    }
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'critical': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getHealthBg = (status) => {
    switch (status) {
      case 'healthy': return 'bg-green-50 border-green-200';
      case 'warning': return 'bg-yellow-50 border-yellow-200';
      case 'critical': return 'bg-red-50 border-red-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Group sync status by source and data_type
  const groupedSyncStatus = () => {
    const filtered = syncStatus.filter(item => {
      const sourceMatch = !filterSource || item.source === filterSource;
      const dataTypeMatch = !filterDataType || item.data_type === filterDataType;
      return sourceMatch && dataTypeMatch;
    });

    const groups = {};
    filtered.forEach(item => {
      const key = `${item.source}:${item.data_type}`;
      if (!groups[key]) {
        groups[key] = {
          source: item.source,
          data_type: item.data_type,
          items: []
        };
      }
      groups[key].items.push(item);
    });

    // Sort groups by source, then data_type
    return Object.entries(groups).sort(([keyA], [keyB]) => {
      const [sourceA, dataTypeA] = keyA.split(':');
      const [sourceB, dataTypeB] = keyB.split(':');
      if (sourceA !== sourceB) {
        return sourceA.localeCompare(sourceB);
      }
      return dataTypeA.localeCompare(dataTypeB);
    });
  };

  // Get summary stats for a group
  const getGroupSummary = (items) => {
    const total = items.length;
    const success = items.filter(item => item.sync_status === 'success').length;
    const failed = items.filter(item => item.sync_status === 'failed').length;
    const inProgress = items.filter(item => item.sync_status === 'in_progress').length;
    const totalRecords = items.reduce((sum, item) => sum + (item.synced_records || 0), 0);
    const latestSync = items.length > 0 ? items[0].last_sync : null;
    
    // Calculate distinct pay periods for ADP time-cards
    const distinctPayPeriods = new Set();
    items.forEach(item => {
      if (item.sync_metadata?.pay_periods) {
        item.sync_metadata.pay_periods.forEach(period => {
          distinctPayPeriods.add(period);
        });
      }
    });
    const totalDistinctPayPeriods = distinctPayPeriods.size;

    // Calculate distinct days for Azuga breadcrumbs
    const distinctDays = new Set();
    items.forEach(item => {
      if (item.sync_metadata?.target_date) {
        distinctDays.add(item.sync_metadata.target_date);
      }
    });
    const totalDistinctDays = distinctDays.size;

    return { total, success, failed, inProgress, totalRecords, latestSync, totalDistinctPayPeriods, totalDistinctDays };
  };

  // Check if a sync is in progress for a specific source and data type
  const isSyncInProgress = (source, dataType) => {
    return syncStatus.some(item => 
      item.source === source && 
      item.data_type === dataType && 
      item.sync_status === 'in_progress'
    );
  };

  const filteredConfigs = syncConfigs.filter(item => {
    const sourceMatch = !filterSource || item.source === filterSource;
    const dataTypeMatch = !filterDataType || item.data_type === filterDataType;
    return sourceMatch && dataTypeMatch;
  });

  const uniqueSources = [...new Set(syncStatus.map(item => item.source))];
  const uniqueDataTypes = [...new Set(syncStatus.map(item => item.data_type))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2">Loading bronze layer data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Bronze Layer</h1>
          <p className="text-gray-600">Data Synchronization Management</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('status')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'status'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Sync Status
          </button>
          <button
            onClick={() => setActiveTab('configs')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'configs'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Configurations
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center space-x-4">
          <Filter className="h-5 w-5 text-gray-500" />
          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Sources</option>
            {uniqueSources.map(source => (
              <option key={source} value={source}>{source}</option>
            ))}
          </select>
          <select
            value={filterDataType}
            onChange={(e) => setFilterDataType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Data Types</option>
            {uniqueDataTypes.map(dataType => (
              <option key={dataType} value={dataType}>{dataType}</option>
            ))}
          </select>
          {(filterSource || filterDataType) && (
            <button
              onClick={() => {
                setFilterSource('');
                setFilterDataType('');
              }}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'status' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Sync Status</h3>
            <p className="text-sm text-gray-600">Current status of all data synchronization operations grouped by source and data type</p>
          </div>
          <div className="space-y-1">
            {groupedSyncStatus().map(([groupKey, group]) => {
              const isExpanded = expandedGroups.has(groupKey);
              const summary = getGroupSummary(group.items);
              const available = availableSyncStates[groupKey] ?? summary.total;

              return (
                <div key={groupKey} className="border-b border-gray-100 last:border-b-0">
                  {/* Group Header */}
                  <div 
                    className="px-6 py-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                    onClick={() => toggleGroup(groupKey)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-gray-500" />
                        )}
                        <Database className="h-5 w-5 text-gray-400" />
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {group.source} - {group.data_type}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {available > summary.total
                              ? `Showing ${summary.total.toLocaleString()} of ${available.toLocaleString()} syncs`
                              : `${summary.total} sync${summary.total !== 1 ? 's' : ''}`} •
                            {summary.success > 0 && <span className="text-green-600 ml-1">{summary.success} success</span>}
                            {summary.failed > 0 && <span className="text-red-600 ml-1">{summary.failed} failed</span>}
                            {summary.inProgress > 0 && <span className="text-blue-600 ml-1">{summary.inProgress} in progress</span>}
                            {group.source === 'adp' && group.data_type === 'time-cards' && summary.totalDistinctPayPeriods > 0 && (
                              <span className="text-purple-600 ml-1">• {summary.totalDistinctPayPeriods} pay periods</span>
                            )}
                            {group.source === 'azuga' && group.data_type === 'vehicle-breadcrumbs' && summary.totalDistinctDays > 0 && (
                              <span className="text-orange-600 ml-1">• {summary.totalDistinctDays} days</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {summary.totalRecords.toLocaleString()} records
                          </p>
                          <p className="text-xs text-gray-500">
                            Last: {summary.latestSync ? formatDate(summary.latestSync) : 'Never'}
                          </p>
                        </div>
                        {isAdmin() && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startSync(group.source, group.data_type);
                          }}
                          disabled={isSyncInProgress(group.source, group.data_type)}
                          className={`flex items-center px-3 py-1 rounded transition-colors ${
                            isSyncInProgress(group.source, group.data_type)
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                        >
                          <Play className="h-4 w-4 mr-1" />
                          {isSyncInProgress(group.source, group.data_type) ? 'Syncing...' : 'Sync'}
                        </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            {group.source === 'azuga' && group.data_type === 'vehicle-breadcrumbs' ? (
                            <>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Sync Time
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Target Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Sync Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Applied to Silver
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Status
                              </th>
                            </>
                          ) : group.source === 'azuga' ? (
                            <>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Sync Time
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Sync Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Records
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Applied to Silver
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Status
                              </th>
                            </>
                          ) : group.source === 'adp' && group.data_type === 'time-cards' ? (
                            <>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Sync Time
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Sync Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Records
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Pay Periods
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Applied to Silver
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Status
                              </th>
                            </>
                          ) : group.source === 'servicetitan' ? (
                            <>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Sync Time
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Sync Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Records
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Tenant ID
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Applied to Silver
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Status
                              </th>
                            </>
                          ) : group.source === 'adp' ? (
                            <>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Sync Time
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Sync Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Records
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Applied to Silver
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Status
                              </th>
                            </>
                          ) : (
                            <>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Sync Time
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Records
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Tenant ID
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Applied to Silver
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top">
                                Status
                              </th>
                            </>
                          )}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {group.items.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              {group.source === 'azuga' && group.data_type === 'vehicle-breadcrumbs' ? (
                                <>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    {item.last_sync ? formatDate(item.last_sync) : 'Never'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    {item.sync_metadata?.target_date || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    <span className={getSyncTypeBadge(item.sync_type || 'unknown')}>
                                      {item.sync_type || 'unknown'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    <span className={getAppliedToOperationalBadge(item.operational_status?.is_current || false)}>
                                      {item.operational_status?.is_current ? 'Yes' : 'No'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap align-top">
                                    <div className="flex items-center">
                                      {getStatusIcon(item.sync_status)}
                                      <span className={`ml-2 ${getStatusBadge(item.sync_status)}`}>
                                        {item.sync_status}
                                      </span>
                                    </div>
                                  </td>
                                </>
                              ) : group.source === 'azuga' ? (
                                <>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    {item.last_sync ? formatDate(item.last_sync) : 'Never'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    <span className={getSyncTypeBadge(item.sync_type || 'unknown')}>
                                      {item.sync_type || 'unknown'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    {(item.synced_records || 0).toLocaleString()}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    <span className={getAppliedToOperationalBadge(item.operational_status?.is_current || false)}>
                                      {item.operational_status?.is_current ? 'Yes' : 'No'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap align-top">
                                    <div className="flex items-center">
                                      {getStatusIcon(item.sync_status)}
                                      <span className={`ml-2 ${getStatusBadge(item.sync_status)}`}>
                                        {item.sync_status}
                                      </span>
                                    </div>
                                  </td>
                                </>
                              ) : group.source === 'adp' && group.data_type === 'time-cards' ? (
                                <>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    {item.last_sync ? formatDate(item.last_sync) : 'Never'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      item.sync_metadata?.sync_type === 'historic' 
                                        ? 'bg-purple-100 text-purple-800' 
                                        : 'bg-blue-100 text-blue-800'
                                    }`}>
                                      {item.sync_metadata?.sync_type || 'unknown'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    {(item.synced_records || 0).toLocaleString()}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    <div className="max-w-xs">
                                      {item.sync_metadata?.pay_periods ? (
                                        <div>
                                          <span className="font-medium text-gray-700">
                                            {item.sync_metadata.pay_period_count || 0} periods
                                          </span>
                                          <div className="text-xs text-gray-500 mt-1">
                                            {item.sync_metadata.pay_periods.map((period, idx) => (
                                              <div key={idx}>{period}</div>
                                            ))}
                                          </div>
                                        </div>
                                      ) : (
                                        <span className="text-gray-400">No pay periods</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    <span className={getAppliedToOperationalBadge(item.operational_status?.is_current || false)}>
                                      {item.operational_status?.is_current ? 'Yes' : 'No'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap align-top">
                                    <div className="flex items-center">
                                      {getStatusIcon(item.sync_status)}
                                      <span className={`ml-2 ${getStatusBadge(item.sync_status)}`}>
                                        {item.sync_status}
                                      </span>
                                    </div>
                                  </td>
                                </>
                              ) : group.source === 'adp' ? (
                                <>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    {item.last_sync ? formatDate(item.last_sync) : 'Never'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    <span className={getSyncTypeBadge(item.sync_type || 'unknown')}>
                                      {item.sync_type || 'unknown'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    {(item.synced_records || 0).toLocaleString()}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    <span className={getAppliedToOperationalBadge(item.operational_status?.is_current || false)}>
                                      {item.operational_status?.is_current ? 'Yes' : 'No'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap align-top">
                                    <div className="flex items-center">
                                      {getStatusIcon(item.sync_status)}
                                      <span className={`ml-2 ${getStatusBadge(item.sync_status)}`}>
                                        {item.sync_status}
                                      </span>
                                    </div>
                                  </td>
                                </>
                              ) : group.source === 'servicetitan' ? (
                                <>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    {item.last_sync ? formatDate(item.last_sync) : 'Never'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    <span className={getSyncTypeBadge(item.sync_type || 'unknown')}>
                                      {item.sync_type || 'unknown'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    {(item.synced_records || 0).toLocaleString()}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 align-top">
                                    {item.tenant_id || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    <span className={getAppliedToOperationalBadge(item.operational_status?.is_current || false)}>
                                      {item.operational_status?.is_current ? 'Yes' : 'No'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap align-top">
                                    <div className="flex items-center">
                                      {getStatusIcon(item.sync_status)}
                                      <span className={`ml-2 ${getStatusBadge(item.sync_status)}`}>
                                        {item.sync_status}
                                      </span>
                                    </div>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    {item.last_sync ? formatDate(item.last_sync) : 'Never'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    {(item.synced_records || 0).toLocaleString()}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 align-top">
                                    {item.tenant_id || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                    <span className={getAppliedToOperationalBadge(item.operational_status?.is_current || false)}>
                                      {item.operational_status?.is_current ? 'Yes' : 'No'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap align-top">
                                    <div className="flex items-center">
                                      {getStatusIcon(item.sync_status)}
                                      <span className={`ml-2 ${getStatusBadge(item.sync_status)}`}>
                                        {item.sync_status}
                                      </span>
                                    </div>
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'configs' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Sync Configurations</h3>
            <p className="text-sm text-gray-600">Manage sync schedules and settings</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schedule
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredConfigs.map((config, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Database className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">{config.source}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {config.data_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{config.schedule || 'Manual'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        config.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {config.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-2">
                        {!isAdmin() && <span className="text-gray-400">-</span>}
                        {isAdmin() && (<>
                        <button
                          onClick={() => toggleConfig(config.id)}
                          className={`flex items-center px-3 py-1 rounded transition-colors ${
                            config.is_active
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {config.is_active ? (
                            <>
                              <EyeOff className="h-4 w-4 mr-1" />
                              Disable
                            </>
                          ) : (
                            <>
                              <Eye className="h-4 w-4 mr-1" />
                              Enable
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => startSync(config.source, config.data_type)}
                          disabled={isSyncInProgress(config.source, config.data_type)}
                          className={`flex items-center px-3 py-1 rounded transition-colors ${
                            isSyncInProgress(config.source, config.data_type)
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                        >
                          <Play className="h-4 w-4 mr-1" />
                          {isSyncInProgress(config.source, config.data_type) ? 'Running...' : 'Run Now'}
                        </button>
                        </>)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      {isAdmin() && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => startSync('servicetitan')}
          className="flex items-center justify-center px-6 py-4 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
        >
          <Play className="h-5 w-5 mr-2" />
          Sync ServiceTitan
        </button>
        <button
          onClick={() => startSync('adp')}
          className="flex items-center justify-center px-6 py-4 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
        >
          <Play className="h-5 w-5 mr-2" />
          Sync ADP
        </button>
        <button
          onClick={() => startSync('azuga')}
          className="flex items-center justify-center px-6 py-4 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
        >
          <Play className="h-5 w-5 mr-2" />
          Sync Azuga
        </button>
      </div>
      )}
    </div>
  );
};

export default BronzeLayer; 